import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type QueryState,
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Exercises, Lessons } from '../type';

import { checkRole, trimValue } from 'helpers';
import { setUrlParams } from 'helpers/url';
import { useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { useUpdateQueryState } from 'hooks/url';
import { cloneDeep, debounce, isUndefined } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useExercisesController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { institutionId } = params;

  useUpdateQueryState(TABLE_KEY.EXERCISES, 12);

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.EXERCISES]);

  const userProfile = useAuthStore(userProfileSelector);

  const [exercisesData, setExercisesData] = useState<Exercises[]>([]);

  const [selectedExercises, setSelectedExercises] = useState<Record<string, string>>({});

  const [lessonsData, setLessonsData] = useState<Lessons[]>([]);

  const searchValue = useString('');

  const searchExercise = useString('');

  const total = useNumber(0);

  const exerciseSelectedId = useString('');

  const [levelLesson, setLevelLesson] = useState<number>(0);

  const selectedLessonId = useString('');

  useEffect(() => {
    if (!userProfile?.id) {
      return;
    }

    if (queryState) {
      setUrlParams(queryState);

      searchExercise.setValue(queryState.search ?? '');

      getListExercises();
    }
  }, [JSON.stringify(queryState), userProfile]);

  useEffect(() => {
    getListLessons();

    updateQueryStateTable(TABLE_KEY.EXERCISES, {
      ...initialQueryState,
      limit: 12,
    });

    return () => {
      updateQueryStateTable(TABLE_KEY.EXERCISES, {
        ...initialQueryState,
        limit: 12,
      });
    };
  }, []);

  const getListLessons = async (search: string = '') => {
    const result: {
      items: Lessons[];
      total: number;
    } = await apiCall({
      url: '/lessons',
      method: 'GET',
      data: {
        page: 1,
        limit: 10,
        institutionId: userProfile?.institution?.id ?? institutionId,
        search: search,
      },
    });

    if (result?.items) {
      setLessonsData(result?.items ?? []);
    }
  };

  const getListExercises = async () => {
    const result: {
      items: Exercises[];
      total: number;
    } = await apiCall({
      url: '/exercises',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...queryState,
        institutionId: userProfile?.institution?.id ?? institutionId,
      },
    });

    if (result?.items) {
      setExercisesData(result?.items ?? []);
    }

    if (result?.total) {
      total.setValue(result.total);
    }
  };

  const updateQueryState = (key: string, newState: QueryState) => {
    updateQueryStateTable(key, newState);
  };

  const onChangeSelectedExercises = (exercises: string) => () => {
    const newSelected = cloneDeep(selectedExercises);

    if (!isUndefined(newSelected[exercises])) {
      delete newSelected[exercises];
    } else {
      newSelected[exercises] = exercises;
    }

    setSelectedExercises(newSelected);
  };

  const callbackFirstPage = () => {
    updateQueryStateTable(TABLE_KEY.EXERCISES, {
      ...initialQueryState,
      page: 1,
      limit: 12,
    });
  };

  const handleDelete = async () => {
    const result: string[] = [];

    for (const el of Object.keys(selectedExercises)) {
      const response: { id: string } = await apiCall({
        url: `/exercises/${selectedExercises[el]}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      if (response?.id) {
        result.push(response.id);
      }
    }

    if (result.length === Object.keys(selectedExercises).length) {
      showToastMessage('success', SUCCESS_MESSAGE.DELETE_EXERCISES);
      callbackFirstPage();
    }

    setSelectedExercises({});
    getListExercises();
  };

  const showLevelLessonModal = (lessonId: string) => {
    openModal(MODAL_KEY.LEVEL_LESSON);
    selectedLessonId.setValue(lessonId);
  };

  const handleAddToLesson = async () => {
    for (const el of Object.keys(selectedExercises)) {
      await apiCall({
        url: '/lessons-exercises',
        method: 'POST',
        data: {
          exerciseId: selectedExercises[el],
          lessonId: selectedLessonId.value,
          lessonLevel: levelLesson,
        },
        isLoading: true,
        showError: true,
      });
    }

    showToastMessage('success', SUCCESS_MESSAGE.ADD_EXERCISE_TO_LESSON);

    closeModal(MODAL_KEY.LEVEL_LESSON);

    setLevelLesson(0);
  };

  const onChangeSearchExcercise = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchExercise.setValue(event.target.value);
    debounceSearch(event.target.value);
  };

  const debounceSearch = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.EXERCISES, {
        ...queryState,
        search: trimValue(newSearch),
        page: 1,
      });
    }, 500),
    [],
  );

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      getListLessons(trimValue(newSearch));
    }, 500),
    [],
  );

  const handleEditExercise = (exerciseId: string) => () => {
    navigationTo(PATH.EDIT_EXERCISES.replace(':id', exerciseId));
  };

  const handleDeleteExercise = (exerciseId: string) => async () => {
    openModal(MODAL_KEY.DELETE_EXERCISE);
    exerciseSelectedId.setValue(exerciseId);
  };

  const onDeleteExercise = async () => {
    const responsive: {
      statusCode: number;
    } = await apiCall({
      url: `/exercises/${exerciseSelectedId.value}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    closeModal(MODAL_KEY.DELETE_EXERCISE);

    if (responsive.statusCode === 400) {
      return;
    }

    if (queryState.page === 1) {
      getListExercises();
    }

    callbackFirstPage();

    setSelectedExercises({});
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_EXERCISE);
  };

  const goToExerciseDetail = (exerciseId: string) => {
    navigationTo(PATH.EDIT_EXERCISES.replace(':id', exerciseId));
  };

  const hideAction = checkRole([RoleKey.TRAINER_INSTITUTION, RoleKey.TRAINER_BIOLOGIC]);

  const handleDuplicateExercise = (exerciseId: string) => async () => {
    const result: Exercises = await apiCall({
      url: `/exercises/${exerciseId}/duplicate`,
      method: 'POST',
      isLoading: true,
      showError: true,
    });

    if (result?.id) {
      showToastMessage('success', 'Exercise duplicated successfully!');
      getListExercises();
    }
  };

  const onCancel = () => {
    closeModal(MODAL_KEY.LEVEL_LESSON);
    setLevelLesson(0);
  };

  return {
    exercisesData,
    total,
    handleUpdated: getListExercises,
    updateQueryState,
    queryState,
    onChangeSelectedExercises,
    selectedExercises,
    setSelectedExercises,
    lessonsData,
    handleDelete,
    handleAddToLesson,
    searchValue,
    searchExercise,
    onChangeSearchExcercise,
    onChangeSearchValue,
    handleEditExercise,
    handleDeleteExercise,
    goToExerciseDetail,
    onDeleteExercise,
    hideAction,
    handleDuplicateExercise,
    levelLesson,
    setLevelLesson,
    showLevelLessonModal,
    onCancel,
  };
};
